import { FC, useState } from "react"
import { useTranslation } from "react-i18next"

import LocationAddressModal from "components/LocationAddressModal/LocationAddressModal"
import { Coordinates, Guid } from "@/types"
import Address from "models/Address"
import { ICreateRequestLocation } from "models/RequestLocation"
import Location from "models/Location"
import CustomError from "models/CustomError"
import { ErrorCode } from "config/constants"
import toastMessages from "stores/toastMessages"

export interface IShippingOrderAddLocationModalProps {
    params: {
        assetIds: Guid[]
        isUnloading: boolean
    } | null
    onClose: () => void
    createRequestLocation?(assetIds: Guid[], isUnloading: boolean, data: ICreateRequestLocation): Promise<void>
}

const ShippingOrderAddLocationModal: FC<IShippingOrderAddLocationModalProps> = ({
    params,
    onClose,
    createRequestLocation,
}) => {
    const { t } = useTranslation()

    const [addRequestLocationDescriptionError, setAddRequestLocationDescriptionError] = useState<string | null>(null)

    async function handleAddRequestLocation(description: string, coordinates: Coordinates | null, address: Address) {
        if (!coordinates) {
            throw new Error("coordinates is required")
        }
        if (!params)  {
            throw new Error("params is required")
        }
        if (!createRequestLocation) {
            throw new Error("createRequestLocation is required")
        }
        setAddRequestLocationDescriptionError(null)
        try {
            await createRequestLocation(
                params.assetIds,
                params.isUnloading,
                {
                    description,
                    coordinates: new Location({
                        latitude: coordinates.lat,
                        longitude: coordinates.lng,
                    }),
                    address,
                },
            )
            handleClose()
        } catch(e) {
            if (e instanceof CustomError && e.code === ErrorCode.conflict)  {
                setAddRequestLocationDescriptionError(t("shippingOrders.requestLocationAlreadyExists"))
            } else {
                toastMessages.showSystemError()
            }
        }
    }

    function handleClose() {
        setAddRequestLocationDescriptionError(null)
        onClose()
    }

    if (!params || !createRequestLocation) return null

    return (
        <LocationAddressModal
            title={t("shippingOrders.addRequestLocationTitle")}
            coordinatesRequired
            addressRequired
            descriptionError={addRequestLocationDescriptionError}
            onClose={handleClose}
            onSave={handleAddRequestLocation}
        />
    )
}

export default ShippingOrderAddLocationModal
